import moment from 'moment-timezone';
import React, { useImperativeHandle, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import MDatetimePickerView, { PopperPlacement } from './MDatetimePicker.view';

import { DatePickerFormats } from 'utils/enums/time-format';
import { MDateNavigatorDirection } from 'views/MDateNavigator/MDateNavigator.model';
import { MDatetimePickerRef } from './MDatetimePicker.model';

type Props = {
  value?: moment.Moment | null;
  startDate?: moment.Moment | null;
  endDate?: moment.Moment | null;
  selectsEnd?: boolean;
  showTimeSelect?: boolean;
  dateFormat?: DatePickerFormats;
  showNavigator?: boolean;
  disabled?: boolean;
  className?: string;
  timeFormat?: DatePickerFormats;
  valid?: boolean;
  invalid?: boolean;
  name?: string;
  placeholder?: string;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
  minDate?: moment.Moment | null;
  maxDate?: moment.Moment | null;
  selected?: moment.Moment | null;
  selectsStart?: boolean;
  showResetDate?: boolean;
  showYearDropdown?: boolean;
  clearButton?: boolean;
  onChange?: (value: moment.Moment | null) => void;
  popperPlacement?: PopperPlacement;
};

const MDatetimePicker: React.ForwardRefRenderFunction<MDatetimePickerRef, Props> = (
  props: React.PropsWithChildren<Props>,
  ref: React.ForwardedRef<MDatetimePickerRef>,
) => {
  const datePickerRef = useRef<DatePicker>(null);
  const currentDate = new Date();

  const daysToAdd = {
    [MDateNavigatorDirection.Backword]: -1,
    [MDateNavigatorDirection.Forward]: 1,
  };

  useImperativeHandle(ref, () => {
    return {
      setOpen(state: boolean) {
        if (datePickerRef.current) {
          datePickerRef.current.setOpen(state);
        }
      },
    };
  }, [datePickerRef.current]);

  const navigateaAdjacentToDay = (direction: number) => {
    const newDate = props.value?.clone().add({ days: daysToAdd[direction] }).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

    props.onChange?.(newDate || null);
  };

  return (
    <MDatetimePickerView
      value={props.value?.toDate()}
      startDate={props.startDate?.toDate()}
      endDate={props.startDate?.toDate()}
      selectsEnd={props.selectsEnd}
      showTimeSelect={props.showTimeSelect}
      dateFormat={props.dateFormat}
      showNavigator={props.showNavigator}
      disabled={props.disabled}
      className={props.className}
      timeFormat={props.timeFormat}
      valid={props.valid}
      invalid={props.invalid}
      name={props.name}
      placeholder={props.placeholder}
      showTimeSelectOnly={props.showTimeSelectOnly}
      timeIntervals={props.timeIntervals}
      minDate={props.minDate?.toDate()}
      maxDate={props.maxDate?.toDate()}
      selected={props.selected?.toDate()}
      selectsStart={props.selectsStart}
      datePickerRef={datePickerRef}
      showResetDate={props.showResetDate}
      currentDate={currentDate}
      clearButton={props.clearButton}
      onClickClearBtn={() => props.onChange?.(null)} // TODO: Better to manage intreal state as well (for the value) and reset it too
      onClickResetDate={() => props.onChange?.(moment())} // TODO: Better to mange internal state ass well (for the value) and change it too
      onLeftNavigatorClick={() => navigateaAdjacentToDay(MDateNavigatorDirection.Backword)}
      onRightNavigatorClick={() => navigateaAdjacentToDay(MDateNavigatorDirection.Forward)}
      onChange={(date: Date | [Date, Date] | null) => props.onChange?.(moment(date as Date))}
      showYearDropdown={props.showYearDropdown}
      popperPlacement={props.popperPlacement}
    ></MDatetimePickerView>
  );
};

MDatetimePicker.displayName = 'MDatetimePicker';

export default React.forwardRef(MDatetimePicker);
